/**
 * Ordering helpers for published checklist fields.
 *
 * Historical admin Preview (pre-0028) sorted by placement group, then Russian label.
 * Authoring publishes store durable `ordinal`; Preview/GET prefer ordinal when present.
 */

export const PUBLISHED_FIELD_PLACEMENT_ORDER: Readonly<Record<string, number>> = {
  HEADER: 1,
  OBJECT_BLOCK: 2,
  RESULT_BLOCK: 3,
  SIGNATURE_BLOCK: 4,
};

export interface PublishedFieldOrderRow {
  readonly field_key: string;
  readonly label: string;
  readonly placement: string;
  readonly ordinal?: number;
}

/** Pre-0028 TemplateVersionRuntimePreview visual order. */
export function comparePublishedFieldsLegacyPreview(
  a: PublishedFieldOrderRow,
  b: PublishedFieldOrderRow,
): number {
  const pa = PUBLISHED_FIELD_PLACEMENT_ORDER[a.placement] ?? 99;
  const pb = PUBLISHED_FIELD_PLACEMENT_ORDER[b.placement] ?? 99;
  if (pa !== pb) {
    return pa - pb;
  }
  const byLabel = a.label.localeCompare(b.label, 'ru');
  if (byLabel !== 0) {
    return byLabel;
  }
  return a.field_key.localeCompare(b.field_key);
}

export function sortPublishedFieldsLegacyPreview<T extends PublishedFieldOrderRow>(
  fields: readonly T[],
): T[] {
  return [...fields].sort(comparePublishedFieldsLegacyPreview);
}

/** Current Preview/GET presentation order (author/legacy backfilled ordinal). */
export function sortPublishedFieldsByOrdinal<T extends PublishedFieldOrderRow>(
  fields: readonly T[],
): T[] {
  return [...fields].sort((a, b) => {
    const oa =
      typeof a.ordinal === 'number' && Number.isFinite(a.ordinal)
        ? a.ordinal
        : Number.MAX_SAFE_INTEGER;
    const ob =
      typeof b.ordinal === 'number' && Number.isFinite(b.ordinal)
        ? b.ordinal
        : Number.MAX_SAFE_INTEGER;
    if (oa !== ob) {
      return oa - ob;
    }
    return a.field_key.localeCompare(b.field_key);
  });
}

export function sortPublishedFieldsByFieldKey<T extends { readonly field_key: string }>(
  fields: readonly T[],
): T[] {
  return [...fields].sort((a, b) => a.field_key.localeCompare(b.field_key));
}
